// Mock data for testing streaming UI
export interface MockStreamingScenario {
	id: string;
	name: string;
	steps: MockStreamingStep[];
}

export interface MockStreamingStep {
	delay: number; // milliseconds
	content?: string;
	statusMessage?: string;
	isToolUsage?: boolean;
	isComplete?: boolean;
}

export const mockStreamingScenarios: MockStreamingScenario[] = [
	{
		id: 'long-analysis',
		name: 'Long Business Analysis',
		steps: [
			{
				delay: 500,
				statusMessage: 'Käsittelen pyyntöäsi...',
			},
			{
				delay: 1000,
				statusMessage: 'Pyyntösi on käsitelty. Aloitan vastauksen generoinnin...',
			},
			{
				delay: 800,
				statusMessage: 'haetaan yritysten listaa…',
				isToolUsage: true,
			},
			{
				delay: 600,
				statusMessage: 'käsitellään yritystietoja...',
				isToolUsage: true,
			},
			{
				delay: 600,
				content: 'Based on the comprehensive analysis of your company data, I can provide the following insights:\n\n',
			},
			{
				delay: 400,
				content: '## Financial Performance Overview\n\n',
			},
			{
				delay: 600,
				content: 'Your company has shown **strong growth** in the following areas:\n\n',
			},
			{
				delay: 500,
				content: '- **Revenue Growth**: 15% year-over-year increase\n',
			},
			{
				delay: 400,
				content: '- **Market Share**: Expanded by 8% in key segments\n',
			},
			{
				delay: 500,
				content: '- **Customer Retention**: Improved to 92%\n\n',
			},
			{
				delay: 700,
				content: '## Key Recommendations\n\n',
			},
			{
				delay: 600,
				content: '1. **Expand Digital Marketing**: Increase online presence to capture younger demographics\n',
			},
			{
				delay: 500,
				content: '2. **Optimize Supply Chain**: Implement AI-driven logistics for 12% cost reduction\n',
			},
			{
				delay: 600,
				content: '3. **Product Innovation**: Invest in R&D for next-generation offerings\n\n',
			},
			{
				delay: 800,
				content: '## Risk Assessment\n\n',
			},
			{
				delay: 500,
				content: 'The analysis reveals some areas that require attention:\n\n',
			},
			{
				delay: 400,
				content: '- **Market Volatility**: Economic uncertainty may impact Q4 results\n',
			},
			{
				delay: 500,
				content: '- **Competition**: New entrants in the market pose challenges\n',
			},
			{
				delay: 600,
				content: '- **Regulatory Changes**: Upcoming compliance requirements need preparation\n\n',
			},
			{
				delay: 700,
				content: '## Conclusion\n\n',
			},
			{
				delay: 800,
				content:
					'Your company is well-positioned for continued growth. The recommended strategies should be implemented over the next 6-12 months to maximize impact and maintain competitive advantage.',
			},
			{
				delay: 300,
				isComplete: true,
			},
		],
	},
	{
		id: 'tool-heavy',
		name: 'Tool-Heavy Process',
		steps: [
			{
				delay: 300,
				statusMessage: 'Initializing data processing tools...',
				isToolUsage: true,
			},
			{
				delay: 800,
				statusMessage: 'Connecting to external APIs...',
				isToolUsage: true,
			},
			{
				delay: 1200,
				statusMessage: 'Fetching real-time market data...',
				isToolUsage: true,
			},
			{
				delay: 900,
				statusMessage: 'Running sentiment analysis algorithms...',
				isToolUsage: true,
			},
			{
				delay: 700,
				statusMessage: 'Aggregating data from multiple sources...',
				isToolUsage: true,
			},
			{
				delay: 600,
				statusMessage: 'Generating insights using AI models...',
				isToolUsage: true,
			},
			{
				delay: 500,
				content: 'I have successfully processed data from multiple sources. Here are the key findings:\n\n',
			},
			{
				delay: 400,
				content: '📊 **Market Analysis**: Current trends show positive momentum\n',
			},
			{
				delay: 300,
				content: '🎯 **Sentiment Score**: 78% positive market sentiment\n',
			},
			{
				delay: 400,
				content: '📈 **Forecast**: 12% growth projected for next quarter\n',
			},
			{
				delay: 200,
				isComplete: true,
			},
		],
	},
	{
		id: 'quick-response',
		name: 'Quick Response',
		steps: [
			{
				delay: 200,
				content: 'Here is a quick summary of your request:\n\n',
			},
			{
				delay: 300,
				content: '✅ **Status**: All systems operational\n',
			},
			{
				delay: 250,
				content: '📋 **Tasks**: 3 pending items require attention\n',
			},
			{
				delay: 200,
				content: '🔔 **Alerts**: No critical issues detected',
			},
			{
				delay: 100,
				isComplete: true,
			},
		],
	},
	{
		id: 'error-scenario',
		name: 'Error Recovery',
		steps: [
			{
				delay: 400,
				statusMessage: 'Processing your request...',
			},
			{
				delay: 800,
				statusMessage: 'Analyzing data sources...',
				isToolUsage: true,
			},
			{
				delay: 1000,
				statusMessage: 'Encountering connection issues, retrying...',
				isToolUsage: true,
			},
			{
				delay: 600,
				statusMessage: 'Successfully reconnected, continuing analysis...',
				isToolUsage: true,
			},
			{
				delay: 500,
				content: 'I encountered some temporary connection issues but was able to recover. Here are the results:\n\n',
			},
			{
				delay: 400,
				content: 'Despite the initial connectivity problems, I was able to gather the following information:\n\n',
			},
			{
				delay: 300,
				content: '- Data analysis completed successfully\n',
			},
			{
				delay: 300,
				content: '- All systems are now functioning normally\n',
			},
			{
				delay: 300,
				content: '- Your request has been processed completely',
			},
			{
				delay: 200,
				isComplete: true,
			},
		],
	},
];

// Mock streaming function
export const simulateStreamingResponse = async (
	scenario: MockStreamingScenario,
	onUpdate: (content: string, statusMessage?: string, isToolUsage?: boolean, isComplete?: boolean) => void,
): Promise<void> => {
	let accumulatedContent = '';

	for (const step of scenario.steps) {
		await new Promise(resolve => setTimeout(resolve, step.delay));

		if (step.content) {
			accumulatedContent += step.content;
		}

		onUpdate(accumulatedContent, step.statusMessage, step.isToolUsage, step.isComplete);

		if (step.isComplete) {
			break;
		}
	}
};
